import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { api, apiErrorMessage } from "../lib/api";
import { getSessionId, getUserId, getStoredUTM, withUtm, trackClick } from "../lib/utm";
import { Loader2 } from "lucide-react";

const FIELD_LABELS = {
  name: "Full name",
  email: "Email address",
  phone: "Phone number",
  income: "Monthly income",
};

export default function LeadDialog({ open, onOpenChange, product }) {
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);

  if (!product) return null;
  const fields = product.form_fields?.length ? product.form_fields : ["name", "email", "phone"];

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const utm = getStoredUTM();
      const payload = {
        product_id: product.id,
        session_id: getSessionId(),
        user_id: getUserId() || undefined,
        name: form.name,
        email: form.email,
        phone: form.phone,
        income: form.income,
        extras: {},
        ...utm,
        referrer: document.referrer || "",
      };
      await trackClick(product.id);
      const { data } = await api.post("/leads", payload);
      toast.success("Application received — we'll be in touch shortly.");
      onOpenChange(false);
      setForm({});
      if (data?.lead_mode === "form_then_redirect") {
        const url = data?.tracked_redirect_url || withUtm(data?.redirect_url);
        if (url) window.open(url, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="lead-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">{product.cta_label || "Apply"} — {product.title}</DialogTitle>
          <DialogDescription className="text-neutral-500">
            {product.subtitle || `A specialist from ${product.provider} will reach out.`}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 mt-2">
          {fields.map((f) => (
            <div key={f} className="space-y-1.5">
              <Label htmlFor={`lead-${f}`} className="text-xs uppercase tracking-[0.15em] text-neutral-500">
                {FIELD_LABELS[f] || f}
              </Label>
              <Input
                id={`lead-${f}`}
                data-testid={`lead-input-${f}`}
                type={f === "email" ? "email" : f === "phone" ? "tel" : "text"}
                required={["name", "email"].includes(f)}
                value={form[f] || ""}
                onChange={(e) => update(f, e.target.value)}
                className="h-11"
              />
            </div>
          ))}
          <Button
            type="submit"
            data-testid="lead-submit-btn"
            disabled={submitting}
            className="w-full h-11 bg-[#0055FF] hover:bg-[#0044CC] text-white font-medium mt-2"
          >
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {product.cta_label || "Submit"}
          </Button>
          <p className="text-[11px] text-neutral-400 text-center">Powered by Offer Express. UTM data captured for attribution.</p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
